<template>
  <div>
    <p class="mb-6 text-sm leading-relaxed text-black2">
      We'll email you when a date opens up for <span class="font-medium text-black1">{{ props.experienceName }}</span
      >.
    </p>
    <form class="flex flex-col gap-y-4" @submit.prevent="handleSubmit">
      <WrappedTextInput
        v-model="form.email"
        aria-label="Email"
        autocomplete="email"
        label="Email"
        placeholder="you@example.com"
        type="email"
        :aria-invalid="hasEmailError"
        :error="getErrorMessage('email')"
        :lang="'en'"
      />
      <MoleculesFormInputWrapper label="Preferred dates" lang="en" :error="dateRangeErrorMessage" :required="false">
        <PreferredDateRangePicker :from="form.preferredDateFrom" :is-error="hasDateRangeError" :min-date="todayInJst" :on-change="onChangePreferredDates" :to="form.preferredDateTo" />
      </MoleculesFormInputWrapper>
      <AtomsErrorText v-if="serverErrorMessage" data-testid="waitlist-registration-server-error" lang="en" role="alert">{{ serverErrorMessage }}</AtomsErrorText>
      <AtomsPrimaryButton class="mt-2 w-full" data-testid="waitlist-registration-submit" :disabled="isSending" :on-click="handleSubmit">
        {{ !isSending ? 'Subscribe' : 'Subscribing...' }}
      </AtomsPrimaryButton>
      <p class="text-xs leading-relaxed text-black2">
        By subscribing, you agree to the
        <a class="text-gold1 underline" href="https://wabunka-lux.jp/pdf/privacy-policy.pdf" target="_blank">Privacy Policy</a>
        and
        <a class="text-gold1 underline" href="https://wabunka-lux.jp/pdf/terms.pdf" target="_blank">Terms of Use</a>.
      </p>
    </form>
  </div>
</template>

<script lang="ts" setup>
import AtomsErrorText from '@/components/atoms/ErrorText/ErrorText.vue';
import AtomsPrimaryButton from '@/components/atoms/PrimaryButton/PrimaryButton.vue';
import MoleculesFormInputWrapper from '@/components/molecules/FormInputWrapper/FormInputWrapper.vue';
import { getCurrentHyphenatedDate } from '@/utils/date';
import WrappedTextInput from '@/wabunka-src/common/components/stateful/WrappedTextInput/WrappedTextInput.vue';
import PreferredDateRangePicker from '@/wabunka-src/features/Experience/pages/ExperienceDetail/components/stateful/WaitlistRegistrationForm/PreferredDateRangePicker/PreferredDateRangePicker.vue';
import { useWaitlistRegistrationForm } from '@/wabunka-src/features/Experience/pages/ExperienceDetail/composables/form/useWaitlistRegistrationForm';

interface Props {
  experienceId: string;
  /**
   * 通知対象を明示するため、本文にプラン名を出す
   */
  experienceName: string;
  onSuccess: () => void;
}

const props = defineProps<Props>();

const { form, isSending, getErrorMessage, serverErrorMessage, onSubmit } = useWaitlistRegistrationForm(props.experienceId);

// 通知バッチの対象は未来日程のみ。過去日で登録すると完了表示になるのに通知が一度も来ない
const todayInJst = getCurrentHyphenatedDate();

// 希望日程範囲は from / to をひとつの入力グループとして扱うため、エラーも範囲としてまとめて出す
const dateRangeErrorMessage = computed(() => getErrorMessage('preferredDateTo'));
const hasDateRangeError = computed(() => dateRangeErrorMessage.value !== '');
const hasEmailError = computed(() => getErrorMessage('email') !== '');

const onChangePreferredDates = (from: string, to: string) => {
  form.value.preferredDateFrom = from;
  form.value.preferredDateTo = to;
};

const handleSubmit = () => {
  onSubmit(props.onSuccess);
};
</script>
